import { useCallback, useEffect, useMemo } from 'react';

import { useLazyGetUserAccessRightsQuery } from '@app/api/AccessRights';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useLazyGetModulesRightsQuery } from '@app/services/rtkQuery/organization';
import { useAppDispatch, useAppSelector } from '@app/store';
import {
  setAccessRights,
  setLoading,
  setOrgModulesAccessRights,
} from '@app/store/slices/AccessRights/slice';
import { createWSConnection } from '@app/utils';
import {
  invokeWSMethod,
  subscribeToWsEvent,
} from '@app/utils/createWSConnection';

export const useInitNewUserAccessRights = () => {
  const {
    params: { ActiveBranchId, UserId, Database },
  } = useUserPrint();

  const { showNotification } = useNotification();

  const dispatch = useAppDispatch();

  const newAccessRights = useAppSelector(
    ({ newAccessRights }) => newAccessRights
  );

  const isRehydrated = useAppSelector(
    (state) => state._persist?.rehydrated ?? false
  );

  const [
    getModulesRights,
    {
      isFetching: isFetchingModulesRights,
      isLoading: isLoadingModulesRights,
      isUninitialized: isUninitializedModulesRights,
    },
  ] = useLazyGetModulesRightsQuery();

  const [
    getUserAccessRights,
    {
      isLoading: isLoadingAccessRights,
      isUninitialized: isUninitializedAccessRights,
    },
  ] = useLazyGetUserAccessRightsQuery();

  const getUserAccessRightsHandler = useCallback(async () => {
    if (!ActiveBranchId || !UserId) return;
    try {
      const response = await getUserAccessRights({
        ActiveBranchId,
        forUserId: UserId,
      }).unwrap();

      dispatch(setAccessRights(response));
    } catch (error) {
      showNotification(error);
    }
  }, [ActiveBranchId, UserId, dispatch, getUserAccessRights, showNotification]);

  const getModulesRightsHandler = useCallback(async () => {
    if (!ActiveBranchId || !UserId) return;

    try {
      const response = await getModulesRights({ ActiveBranchId }).unwrap();
      dispatch(setOrgModulesAccessRights(response.Modules));
    } catch (error) {
      showNotification(error);
    }
  }, [getModulesRights, ActiveBranchId, UserId, dispatch, showNotification]);

  // The access-rights requests are lazy and only fire once we have the user
  // identity (ActiveBranchId + UserId). Their uninitialized state must NOT be
  // reported as "loading" when we have no identity to trigger them with —
  // otherwise the app hangs on AppLoader forever whenever a token exists but the
  // identity is missing (logout/login and multi-tab redux-persist desync).
  const canRequestRights = Boolean(ActiveBranchId && UserId);

  const isLoading = useMemo(() => {
    // Keep the loader up until redux-persist restores the identity, so the app
    // shell does not flash before we know whether rights can be requested.
    if (!isRehydrated) return true;
    // Rehydrated but no identity: we cannot (and will not) request rights, so we
    // are not loading. Restoring a usable session is handled by the page auth
    // flow (authenticateHandler), which re-auths by cookie or redirects to login.
    if (!canRequestRights) return false;

    return (
      isLoadingAccessRights ||
      isUninitializedAccessRights ||
      isLoadingModulesRights ||
      isFetchingModulesRights ||
      isUninitializedModulesRights
    );
  }, [
    isRehydrated,
    canRequestRights,
    isLoadingAccessRights,
    isUninitializedAccessRights,
    isLoadingModulesRights,
    isFetchingModulesRights,
    isUninitializedModulesRights,
  ]);

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    getUserAccessRightsHandler();
  }, [getUserAccessRightsHandler]);

  useEffect(() => {
    getModulesRightsHandler();
  }, [getModulesRightsHandler]);

  useEffect(() => {
    if (UserId && Database) {
      createWSConnection(async () => {
        await invokeWSMethod('ListenUser', Database, UserId);

        subscribeToWsEvent('OnUserAccessRights', (data) => {
          if (data.isSuccess) {
            const parsedData = JSON.parse(data.jsonData);

            dispatch(setAccessRights(parsedData));
          }
        });
      });
    }
  }, [UserId, Database, ActiveBranchId, dispatch]);

  useEffect(() => {
    if (newAccessRights) {
      localStorage.setItem(
        'NewUserAccessRights',
        JSON.stringify(newAccessRights)
      );
    }
  }, [newAccessRights]);
};
